import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Table from "../../../../core/common/dataTable/index";
import { exam } from "../../../../core/data/json/exam";
import dayjs from "dayjs";
import { all_routes } from "../../../router/all_routes";
import { useUserPermissions } from "../../../UserPermissionsContext";
import Cookies from "js-cookie";
import { Input, Select, Spin } from "antd";
import AudioPlayer from "../audioPlayer";
import { DateRange } from "react-date-range";
import { format } from "date-fns";
import { CloseCircleOutlined, SearchOutlined } from "@ant-design/icons";
import { FilterDropdownProps } from "antd/es/table/interface";

type Permission = { id: number; name: string };
type Admin = {
  student_name: string;
  student_scs: string;
  agent_name: string;
  call_start_time_: string;
  call_end_time_: string;
  call_duration_hms: string;
  conversation_duration_hms: string;
  caller_status: string;
  destination_status: string;
  call_status: string;
  aws_call_recording_file: string;
};

type Agent = {
  id: number;
  username: string;
};

const WizklubCallLessThan1Minute = () => {
  const routes = all_routes;
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const idParam = searchParams.get('id');
  const id = idParam ? parseInt(idParam, 10) : null;
  const initialStartDate = searchParams.get('startDate');
  const initialEndDate = searchParams.get('endDate');
  const data = exam;
  const dropdownMenuRef = useRef<HTMLDivElement | null>(null);
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0"); // Month is zero-based, so we add 1
  const day = String(today.getDate()).padStart(2, "0");
  const formattedDate = `${month}-${day}-${year}`;
  const defaultValue = dayjs(formattedDate);
  const jwtToken = Cookies.get("authToken");
  const { userPermissions, webApi } = useUserPermissions();
  const [isLoading, setIsLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [admin, setAdmin] = useState<Admin[]>([]);
  const [from, setFrom] = useState();
  const [to, setTo] = useState<number>();
  const [totalCount, setTotalCount] = useState();
  const [toastMessage, setToastMessage] = useState("");
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [searchText, setSearchText] = useState<string>("");
  const [playingAudio, setPlayingAudio] = useState<string | null>(null);
  const audioRefs = useRef<{ [key: string]: HTMLAudioElement | null }>({});
  const [showCalendar1, setShowCalendar1] = useState(false);
  const [scsSearchText, setScsSearchText] = useState("");
  const [nameSearchText, setNameText] = useState("");
  const [callerStatus, setCallerStatus] = useState("");
  const [destinationStatus, setDestinationStatus] = useState("");
  const [callStatus, SetCallStatus] = useState("");
  const [selectedAgentFilter, setSelectedAgentFilter] = useState<number>(0);
  const handleClearAgent = () => setSelectedAgentFilter(0);
  const [agentsDropdown, setAgentsDropdown] = useState<Agent[]>([]);
  // const [startDate, setStartDate] = useState<string | null>(null);
  // const [endDate, setEndDate] = useState<string | null>(null);

  const handleClearCallerStatus = () => {
    setCallerStatus("");
  };
  const handleClearDestinationStatus = () => {
    setDestinationStatus("");
  };
  const handleClearCallStatus = () => {
    SetCallStatus("");
  };

  const [dateRange, setDateRange] = useState([
    {
      startDate: initialStartDate
        ? new Date(initialStartDate)
        : new Date(new Date().setHours(0, 0, 0, 0)),
      endDate: initialEndDate
        ? new Date(initialEndDate)
        : new Date(new Date().setHours(0, 0, 0, 0)),
      key: "selection",
    },
  ]);

  const startDate = format(dateRange[0].startDate, "yyyy-MM-dd");
  const endDate = format(dateRange[0].endDate, "yyyy-MM-dd");

  const handlePlayPause = (id: string) => {
    const currentAudio = audioRefs.current[id];

    if (currentAudio) {
      if (playingAudio === id) {
        currentAudio.pause();
        setPlayingAudio(null);
      } else {
        if (playingAudio !== null) {
          const previousAudio = audioRefs.current[playingAudio];
          if (previousAudio) {
            previousAudio.pause();
          }
        }
        currentAudio
          .play()
          .then(() => setPlayingAudio(id))
          .catch((error) => {
            console.error("Error playing audio:", error);
            setPlayingAudio(null);
          });
      }
    }
  };

  // const handleFetchData = () => {
  //   if (startDate && endDate) {
  //     fetchData(id, currentPage, pageSize, searchText, startDate, endDate, scsSearchText,
  //       nameSearchText, selectedAgentFilter, callerStatus, destinationStatus, callStatus); // Call fetchData with date range
  //     // Hide the date range picker after selecting dates
  //     setShowCalendar1(false);
  //   }
  // };

  const checkPermission = (action: any, subject: any) => {
    return userPermissions.some(
      (ability) => ability.action === action && ability.subject === subject
    );
  };

  useEffect(() => {
    if (!checkPermission("view_cvr_inputs", "Records")) {
      navigate(routes.crmLandingPage);
    } else if (id !== null && !isNaN(id)) {
      fetchData(
        id,
        currentPage,
        pageSize,
        searchText,
        startDate || undefined,
        endDate || undefined,
        scsSearchText,
        nameSearchText, selectedAgentFilter, callerStatus, destinationStatus, callStatus
      );
      fetchAgentsData();
    }
  }, [userPermissions]);

  const handleApplyClick = () => {
    if (dropdownMenuRef.current) {
      dropdownMenuRef.current.classList.remove("show");
    }
  };

  const getModalContainer = () => {
    const modalElement = document.getElementById("modal-datepicker");
    return modalElement ? modalElement : document.body; // Fallback to document.body if modalElement is null
  };

  const options = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${jwtToken}`,
    },
  };

  const fetchAgentsData = async () => {
    try {
      const response = await fetch(
        `${webApi}/teammgmt/get_team_members_by_teammgmt/2/`,
        options
      );
      const data = await response.json();
      setAgentsDropdown(data);
    } catch (error) {
      console.error("Failed to fetch agents", error);
    }
  };

  const fetchGroupsData = async (
    id: number,
    page: number,
    pageSize: number,
    searchText: string,
    startDate = "",
    endDate = "",
    scsNumber: string,
    name: string,
    agent: number,
    callerStatus: string,
    destinationStatus: string,
    callStatus: string,
  ) => {
    let apiUrl = `${webApi}/wizklub/cvr_inputs_call_logs/?agent=${id}&duration=less_than_1_min&page=${page}&page_size=${pageSize}&search=${searchText}&SCS_Number=${scsNumber}&name=${name}&caller_status=${callerStatus}&destination_status=${destinationStatus}&call_status=${callStatus}`
    if (agent) apiUrl += `&agent=${agent}`;

    const response = await fetch(
      apiUrl,
      options
    );
    if (!response.ok) {
      if (response.status === 404) {
        return await response.json();
      }
      const errorData = await response.json();
      if (errorData.code === "token_not_valid") {
        navigate("/academic/sri-chaitanya/crm/landing-page");
      }
      throw new Error(errorData.detail || "Failed to fetch user data");
    }
    return await response.json();
  };

  const fetchData = async (
    id: number,
    page: number,
    pageSize: number,
    searchText: string,
    startDate = "",
    endDate = "",
    scsNumber: string,
    name: string,
    agent: number,
    callerStatus: string,
    destinationStatus: string,
    callStatus: string,
  ) => {
    setIsLoading(true); // Start loading
    try {
      const [groupsData] = await Promise.all([
        fetchGroupsData(id, page, pageSize, searchText, startDate, endDate, scsNumber, name, agent, callerStatus, destinationStatus, callStatus),
      ]);
      setTotalPages(groupsData.total_pages);
      setAdmin(groupsData.results);
      setTotalCount(groupsData.total_count);
      setFrom(groupsData.from);
      setTo(groupsData.to);
      setTotalCount(groupsData.total_count);
      setToastMessage(groupsData.message || "submitted successfully!");
      setShowSuccessToast(true);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false); // Stop loading
    }
  };

  useEffect(() => {
    console.log(admin);
  }, [admin])

  const handleTableChange = (pagination: any) => {
    const { current, pageSize } = pagination;
    setCurrentPage(current);
    setPageSize(pageSize);

  };

  const handleSearch = (value: string) => {
    setSearchText(value);

  };

  useEffect(() => {
    if (id !== null && !isNaN(id)) {
      fetchData(
        id,
        currentPage,
        pageSize,
        searchText,
        startDate || undefined,
        endDate || undefined,
        scsSearchText,
        nameSearchText, selectedAgentFilter, callerStatus, destinationStatus, callStatus
      );
    }
  }, [id, currentPage, pageSize, searchText, scsSearchText, nameSearchText, selectedAgentFilter, callerStatus, destinationStatus, callStatus])

  const columns = [
    {
      title: (
        <span>
          SCS Number
          {scsSearchText && (
            <span style={{ marginLeft: 8, display: "inline-flex", alignItems: "center" }}>
              <span style={{ color: "#1890ff", marginRight: 4 }}>({scsSearchText})</span>
              <CloseCircleOutlined
                onClick={() => {
                  setScsSearchText("");
                }}
                style={{ color: "red", cursor: "pointer" }}
              />
            </span>
          )}
        </span>
      ),
      dataIndex: "student_scs",
      key: "student_scs",
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: FilterDropdownProps) => (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            placeholder="Search SCS Number"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => {
              confirm();
              setScsSearchText(selectedKeys[0] as string);
            }}
            style={{ marginBottom: 8, display: "block" }}
          />
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
      ),
      onFilter: (value: string, record: { student_scs: { toString: () => string; }; }) =>
        record.student_scs?.toString().toLowerCase().includes((value as string).toLowerCase()),
    },
    {
      title: (
        <span>
          Name
          {nameSearchText && (
            <span style={{ marginLeft: 8, display: "inline-flex", alignItems: "center" }}>
              <span style={{ color: "#1890ff", marginRight: 4 }}>({nameSearchText})</span>
              <CloseCircleOutlined
                onClick={() => {
                  setNameText("");
                }}
                style={{ color: "red", cursor: "pointer" }}
              />
            </span>
          )}
        </span>
      ),
      dataIndex: "student_name",
      key: "student_name",
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: FilterDropdownProps) => (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            placeholder="Search Name"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => {
              confirm();
              setNameText(selectedKeys[0] as string);
            }}
            style={{ marginBottom: 8, display: "block" }}
          />
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
      ),
      onFilter: (value: string, record: { student_name: { toString: () => string; }; }) =>
        record.student_name?.toString().toLowerCase().includes((value as string).toLowerCase()),
    },
    {
      title: (
        <span>
          Agent{selectedAgentFilter !== 0 ? ` (${agentsDropdown.find(agent => agent.id === selectedAgentFilter)?.username})` : ""}
          {selectedAgentFilter !== 0 && (
            <CloseCircleOutlined
              onClick={handleClearAgent}
              style={{ marginLeft: 8, color: "red", cursor: "pointer" }}
            />
          )}
        </span>

      ),
      dataIndex: "agent_name",
      key: "agent_name",
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }: any) => (
        <div style={{ padding: 8 }}>
          <Select
            showSearch
            placeholder="Select Agent"
            style={{ width: 188 }}
            value={selectedKeys[0]}
            onChange={(value) => {
              const selected = agentsDropdown.find((b) => b.username === value);
              setSelectedKeys(selected ? [selected.username] : []);
              setSelectedAgentFilter(value);
              confirm();
            }}
            allowClear
            filterOption={(input, option) =>
              typeof option?.children === 'string' && (option.children as string).toLowerCase().includes(input.toLowerCase())
            }
          >
            {agentsDropdown.map((branch) => (
              <Select.Option key={branch.id} value={branch.id}>
                {branch.username}
              </Select.Option>
            ))}
          </Select>
        </div>
      ),
      onFilter: (value: any, record: { agent_name: string }) => record.agent_name === value,
    },

    {
      title: "Call Start Time",
      dataIndex: "call_start_time_",
    },
    {
      title: "Call End Time",
      dataIndex: "call_end_time_",
    },
    {
      title: "Call Duration (HMS)",
      dataIndex: "call_duration_hms",
    },
    {
      title: "Conversation Duration (HMS)",
      dataIndex: "conversation_duration_hms",
    },
    {
      title: (
        <span>
          Caller Status
          {callerStatus !== "" && (
            <>
              {` (${callerStatus})`}
              <CloseCircleOutlined
                onClick={handleClearCallerStatus}
                style={{ marginLeft: 8, color: "red", cursor: "pointer" }}
              />
            </>
          )}
        </span>
      ),
      dataIndex: "caller_status",
      key: "caller_status",
      // render: (value: boolean) => (value ? "Yes" : "No"),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }: any) => (
        <div style={{ padding: 8 }}>
          <Select
            placeholder="Caller Status"
            style={{ width: 188 }}
            value={selectedKeys[0]}
            onChange={(value) => {
              setSelectedKeys([value]);
              setCallerStatus(value); // <-- Updated state handler
              confirm();
            }}
            allowClear
          >
            <Select.Option value="Answered">Answered</Select.Option>
            <Select.Option value="Missed">Missed</Select.Option>
          </Select>
        </div>
      ),
      onFilter: (value: boolean, record: { caller_status: boolean }) =>
        record.caller_status === value,
    },
    {
      title: (
        <span>
          Destination Status
          {destinationStatus !== "" && (
            <>
              {` (${destinationStatus})`}
              <CloseCircleOutlined
                onClick={handleClearDestinationStatus}
                style={{ marginLeft: 8, color: "red", cursor: "pointer" }}
              />
            </>
          )}
        </span>
      ),
      dataIndex: "destination_status",
      key: "destination_status",
      // render: (value: boolean) => (value ? "Yes" : "No"),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }: any) => (
        <div style={{ padding: 8 }}>
          <Select
            placeholder="Destination Status"
            style={{ width: 188 }}
            value={selectedKeys[0]}
            onChange={(value) => {
              setSelectedKeys([value]);
              setDestinationStatus(value); // <-- Updated state handler
              confirm();
            }}
            allowClear
          >
            <Select.Option value="Answered">Answered</Select.Option>
            <Select.Option value="Missed">Missed</Select.Option>
          </Select>
        </div>
      ),
      onFilter: (value: boolean, record: { destination_status: boolean }) =>
        record.destination_status === value,
    },
    {
      title: (
        <span>
          Call Status
          {callStatus !== "" && (
            <>
              {` (${callStatus})`}
              <CloseCircleOutlined
                onClick={handleClearCallStatus}
                style={{ marginLeft: 8, color: "red", cursor: "pointer" }}
              />
            </>
          )}
        </span>
      ),
      dataIndex: "call_status",
      key: "call_status",
      // render: (value: boolean) => (value ? "Yes" : "No"),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }: any) => (
        <div style={{ padding: 8 }}>
          <Select
            placeholder="Call Status"
            style={{ width: 188 }}
            value={selectedKeys[0]}
            onChange={(value) => {
              setSelectedKeys([value]);
              SetCallStatus(value); // <-- Updated state handler
              confirm();
            }}
            allowClear
          >
            <Select.Option value="Answered">Answered</Select.Option>
            <Select.Option value="Missed">Missed</Select.Option>
          </Select>
        </div>
      ),
      onFilter: (value: boolean, record: { call_status: boolean }) =>
        record.call_status === value,
    },
    {
      title: "AWS Call Recording File",
      dataIndex: "aws_call_recording_file",
      render: (aws_call_recording_file: string | null) =>
        aws_call_recording_file ? (
          <AudioPlayer src={aws_call_recording_file} />
        ) : null,
    },
  ];

  const [loading, setLoading] = useState(false);

  const handleExportClick = async () => {

    let apiUrl = `${webApi}/exports/export_cvr_inputs_call_logs/?agent=${id}&connection_status=Answered&duration=less_than_1_min&token=${jwtToken}&SCS_Number=${scsSearchText}&name=${nameSearchText}&caller_status=${callerStatus}&destination_status=${destinationStatus}&call_status=${callStatus}`;
    if (selectedAgentFilter) apiUrl += `&agent=${selectedAgentFilter}`;

    window.location.href = apiUrl;

  };

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">
                Wizklub calls Less Than 1 Minute
              </h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.wizklubDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={routes.wizklubInputs}>Wizkklub Inputs</Link>
                  </li>
                  {/* <li className="breadcrumb-item">
                    <Link to={routes.wizklubAgentCallSummary}>Call Summary</Link>
                  </li> */}
                  <li className="breadcrumb-item active" aria-current="page">
                    Wizklub calls Less Than 1 Minute
                  </li>
                </ol>
              </nav>
            </div>
           <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              {checkPermission("can_export_wizklub_agent_inputs", "Exports") && (
                <button className="btn btn-primary me-2" onClick={handleExportClick} disabled={loading}>
                  {loading ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  ) : (
                    "Export"
                  )}
                </button>
              )}
            </div>
          </div>
          {/* /Page Header */}
          {/* Guardians List */}
          <div
            className="card "
            style={{
              width: "100%",
              boxShadow: "0 4px 8px 0 rgba(0,0,0,0.2)",
              borderRadius: "10px",
            }}
          >
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">Wizklub call logs List</h4>
              <div
                className="d-flex align-items-center flex-wrap"
                style={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <div
                  className="d-flex align-items-center"
                  style={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >

                </div>
              </div>
            </div>
            <div className="card-body p-0 py-3">
              <div
                className="serch-global text-right"
                style={{ marginRight: "15px" }}
              >
                <input
                  type="search"
                  className="form-control form-control-sm mb-3 w-auto float-end"
                  value={searchText}
                  placeholder="Search"
                  onChange={(e) => handleSearch(e.target.value)}
                  aria-controls="DataTables_Table_0"
                ></input>
              </div>
              {isLoading ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-start",
                    height: "100vh",
                    paddingTop: "20px",
                  }}
                >
                  <Spin /> {/* Display loader while loading */}
                </div>
              ) : (
                <Table
                  columns={columns}
                  dataSource={admin}
                  Selection={false}
                  pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    locale: { items_per_page: "" },
                    nextIcon: <span>Next</span>,
                    prevIcon: <span>Prev</span>,
                    defaultPageSize: 10,
                    showSizeChanger: true,
                    total: totalCount,
                    showTotal: (total: any, range: any[]) =>
                      `${range[0]}-${range[1]} of ${total}`,
                    pageSizeOptions: ["10", "20", "30"],
                  }}
                  onChange={handleTableChange} // Move this outside of pagination
                  onSearch={handleSearch}
                />
              )}
            </div>
          </div>
          {/* /Guardians List */}
        </div>
      </div>
      <>
        {/* Delete Modal */}
        <div className="modal fade" id="delete-modal">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <form>
                <div className="modal-body text-center">
                  <span className="delete-icon">
                    <i className="ti ti-trash-x" />
                  </span>
                  <h4>Confirm Deletion</h4>
                  <p>
                    You want to delete all the marked items, this cant be undone
                    once you delete.
                  </p>
                  <div className="d-flex justify-content-center">
                    <Link
                      to="#"
                      className="btn btn-light me-3"
                      data-bs-dismiss="modal"
                    >
                      Cancel
                    </Link>
                    <Link
                      to="#"
                      data-bs-dismiss="modal"
                      className="btn btn-danger"
                    >
                      Yes, Delete
                    </Link>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
        {/* /Delete Modal */}
      </>
    </div>
  );
};

export default WizklubCallLessThan1Minute;
